import { analytics } from './analytics';

/**
 * Navigation Utilities
 * 
 * Helper functions for consistent navigation behavior and analytics tracking
 */

/**
 * Smoothly scroll to an element by ID with analytics tracking
 */
export const scrollToElement = (
  elementId: string, 
  source: string = 'unknown',
  offset: number = 0
): void => {
  const element = document.getElementById(elementId);
  
  if (element) {
    const elementPosition = element.offsetTop - offset;
    
    window.scrollTo({
      top: elementPosition,
      behavior: 'smooth'
    });
    
    // Track the scroll action
    analytics.trackFeatureUsage('navigation', `scroll-to-${elementId}`);
    analytics.trackButtonClick(`Scroll to ${elementId}`, source);
  } else {
    console.warn(`Element with ID '${elementId}' not found`);
  }
};

/**
 * Scroll to top of page with analytics tracking
 */
export const scrollToTop = (source: string = 'unknown'): void => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth'
  });
  
  analytics.trackFeatureUsage('navigation', 'scroll-to-top');
  analytics.trackButtonClick('Scroll to top', source);
};

/**
 * Handle hash-based navigation within the same page
 */
export const navigateToHash = (hash: string, source: string = 'unknown'): void => {
  // Remove the # if it's included
  const elementId = hash.startsWith('#') ? hash.slice(1) : hash;
  
  // Update URL hash without triggering a page reload
  if (window.location.hash !== `#${elementId}`) {
    window.history.pushState(null, '', `#${elementId}`);
  }
  
  // Scroll to the element
  scrollToElement(elementId, source);
};

/**
 * Enhanced click handler for navigation links
 */
export const createNavClickHandler = (
  href: string, 
  source: string = 'nav',
  callback?: () => void
) => {
  return (event: React.MouseEvent) => {
    // If it's a hash link (internal page navigation)
    if (href.startsWith('#')) {
      event.preventDefault();
      navigateToHash(href, source);
    }
    // For external links or regular page navigation, let React Router handle it
    
    // Execute any additional callback
    if (callback) {
      callback();
    }
  };
};

/**
 * Detect if user is near bottom of page (for infinite scroll, etc.)
 */
export const isNearBottom = (threshold: number = 100): boolean => {
  return window.innerHeight + window.scrollY >= document.body.offsetHeight - threshold;
};

/**
 * Get current scroll position as percentage
 */
export const getScrollPercentage = (): number => {
  const scrollTop = window.pageYOffset;
  const docHeight = document.body.offsetHeight - window.innerHeight;
  return (scrollTop / docHeight) * 100;
};

/**
 * Smooth scroll with custom easing and duration
 */
export const smoothScrollTo = (
  targetY: number, 
  duration: number = 800,
  easing: (t: number) => number = (t: number) => t * (2 - t) // easeOutQuad
): Promise<void> => {
  return new Promise((resolve) => {
    const startY = window.pageYOffset;
    const difference = targetY - startY;
    const startTime = performance.now();

    const step = (currentTime: number) => {
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      const easedProgress = easing(progress);
      
      window.scrollTo(0, startY + difference * easedProgress);
      
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        resolve();
      }
    };
    
    requestAnimationFrame(step);
  });
};

export default {
  scrollToElement,
  scrollToTop,
  navigateToHash,
  createNavClickHandler,
  isNearBottom,
  getScrollPercentage,
  smoothScrollTo
};
